import * as React from 'react'
import { ComponentProps, JSX, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useFiltersApi } from '../../../hooks'

import { FilterProps } from '../../common/Filter/types'
import { SelectFilter } from '../../common/Filter/SelectFilter/SelectFilter'
import { type Option } from '../../common/Form/types'

import { useFilterLabels } from './useFilterLabels'
import { GetUSNsRequestParams } from '@/hooks/useApi/useFiltersApi/types'
import { type USNAccount } from '@/hooks/useApi/types'
import {
    formatFirstFacilityAddressField,
    formatSecondFacilityAddressField,
} from '@/utils/formatFacilityAddress'

export type USNFilterProps = {
    name: string
    onChange: (contract: string[]) => void
    value: string[]
    defaultValue: string[]
    valueLabel?: string
    params: Partial<GetUSNsRequestParams>
    className?: string
} & Pick<
    FilterProps,
    'containerRef' | 'disabled' | 'disabledLabel' | 'notAvailable'
>

export const USNFilter: React.FC<USNFilterProps> = (props) => {
    const { value, defaultValue, name, notAvailable, params, onChange } = props
    const { useUSNsQuery } = useFiltersApi()

    const query = useUSNsQuery(params)
    const { data, isLoading, isError, isFetched, isFetching } = query
    const labels = useFilterLabels()
    const { t } = useTranslation('translations')

    const optionMap = React.useMemo<Map<string, USNAccount>>(() => {
        if (!data) return new Map<string, USNAccount>()
        return new Map(data.map((usn) => [usn.usnAccountId.toString(), usn]))
    }, [data])

    const options: Option[] | undefined = data?.map((usn) => {
        return {
            value: usn.usnAccountId.toString(),
            label: usn.usnAccountName,
        }
    })

    const renderRichLabel = useCallback(
        (
            option: Option,
            highlightMatchingText?: (input: string) => string | JSX.Element
        ) => {
            if (typeof option.value !== 'string') return option.label

            const rawOption = optionMap.get(option.value)

            if (rawOption) {
                const firstAddressField = formatFirstFacilityAddressField(
                    rawOption.facilityAddress
                )
                const secondAddressField = formatSecondFacilityAddressField(
                    rawOption.facilityAddress
                )
                return (
                    <div className="space-y-0.5">
                        <div>
                            {highlightMatchingText?.(
                                rawOption.usnAccountName
                            ) ?? rawOption.usnAccountName}
                        </div>
                        <div className="text-body-small text-subtle">
                            {highlightMatchingText?.(firstAddressField) ??
                                firstAddressField}
                        </div>
                        {secondAddressField && (
                            <div className="text-body-small text-subtle">
                                {highlightMatchingText?.(secondAddressField) ??
                                    secondAddressField}
                            </div>
                        )}
                    </div>
                )
            }

            return option.label
        },
        [optionMap]
    )

    const useSearchParams = useCallback(
        (searchValue: string) => ({
            options: options,
            compareFn: (option: Option) => {
                if (typeof option.value !== 'string') return false

                const rawOption = optionMap.get(option.value)
                if (!rawOption) {
                    return false
                }
                const firstAddressField = formatFirstFacilityAddressField(
                    rawOption.facilityAddress
                )
                const secondAddressField = formatSecondFacilityAddressField(
                    rawOption.facilityAddress
                )

                return (
                    rawOption.usnAccountName
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim()) ||
                    firstAddressField
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim()) ||
                    secondAddressField
                        .toLowerCase()
                        .trim()
                        .includes(searchValue.toLowerCase().trim())
                )
            },
        }),
        [options, optionMap]
    )

    const passedProps: ComponentProps<typeof SelectFilter> = {
        name,
        onChange: onChange,
        config: {
            value: value,
            options: options,
        },
        searchConfig: {
            name: `${name}-search`,
            placeholder: t('filters.usn.searchPlaceholder'),
        },
        value: value,
        defaultValue: defaultValue,
        renderRichLabel,
        label: t(`filters.usn.label`),
        disabled: props.disabled,
        disabledLabel: props.disabledLabel,
        notAvailable,
        useSearchParams,
        ...labels,
        isLoading: isLoading || isFetching || !isFetched,
        isError,
        valueLabel: props.valueLabel,
        containerRef: props.containerRef,
        className: props.className,
    }

    return <SelectFilter {...passedProps} />
}
